import type { FavoriteShortcut, Project, Task } from '@portfolio-pm/shared';
import { INBOX_PROJECT_ID } from '@portfolio-pm/yjs-doc';
import { Bot, CheckSquare2, Crosshair, FolderKanban, Plus, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { AppRoute } from '../../app/App.tsx';
import { getDocStore } from '../../store/doc-store.ts';
import {
  type TaskSystemViewId,
  type TaskWorkspaceView,
  buildTaskWorkspaceCounts,
} from '../tasks/task-workspace-model.ts';
import { ScrollViewport } from './ScrollViewport.tsx';
import { SmartListForm } from './sidebar/SmartListForm.tsx';
import {
  ProjectSectionMenu,
  RouteButton,
  SavedViewsSectionMenu,
  SidebarRowButton,
  SidebarSection,
} from './sidebar/sidebar-menus.tsx';
import {
  DEFAULT_TAG_COLORS,
  type FavoriteItem,
  FavoriteRow,
  NameInput,
  ProjectRow,
  SmartListRow,
  TagRow,
  hashTag,
  shortcutKey,
} from './sidebar/sidebar-rows.tsx';

export interface SidebarProps {
  route: AppRoute;
  taskView: TaskWorkspaceView;
  mobileHidden?: boolean;
  onNavigate(route: AppRoute): void;
  onSelectTaskView(view: TaskWorkspaceView): void;
}

const SYSTEM_VIEWS: Array<{ id: TaskSystemViewId; label: string }> = [
  { id: 'all', label: 'All Tasks' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'This Week' },
  { id: 'trash', label: 'Trash' },
];

export function Sidebar({
  route,
  taskView,
  mobileHidden = false,
  onNavigate,
  onSelectTaskView,
}: SidebarProps): JSX.Element {
  const store = getDocStore();
  const projects = store.useProjects();
  const allProjects = store.useAllProjects();
  const smartLists = store.useSmartLists();
  const tags = store.useTags();
  const active = store.useActiveProject();
  const snapshot = store.useFocusSnapshot();
  const uiPreferences = store.useUiPreferences();

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [creatingSmartList, setCreatingSmartList] = useState(false);
  const [editingSmartListId, setEditingSmartListId] = useState<string | undefined>(undefined);
  const [editingTag, setEditingTag] = useState<string | undefined>(undefined);
  const [draggingFavoriteKey, setDraggingFavoriteKey] = useState<string | undefined>(undefined);
  const [draggingProjectId, setDraggingProjectId] = useState<string | undefined>(undefined);

  const counts = useMemo(
    () => buildTaskWorkspaceCounts(snapshot.tasks, projects, smartLists, Date.now()),
    [projects, smartLists, snapshot.tasks],
  );
  const sidebarProjectCounts = useMemo(
    () => buildProjectSidebarCounts(snapshot.tasks, allProjects, uiPreferences.projectSidebarCount),
    [allProjects, snapshot.tasks, uiPreferences.projectSidebarCount],
  );
  const visibleProjects = useMemo(
    () =>
      sortSidebarProjects(
        allProjects.filter(
          (project) => uiPreferences.projectSidebarShowArchived || !project.archived,
        ),
        uiPreferences.projectSidebarSort,
      ),
    [allProjects, uiPreferences.projectSidebarShowArchived, uiPreferences.projectSidebarSort],
  );
  const favoriteShortcuts = uiPreferences.favoriteShortcuts;
  const favoriteKeys = useMemo(
    () => new Set(favoriteShortcuts.map(shortcutKey)),
    [favoriteShortcuts],
  );
  const visibleSmartLists = useMemo(
    () =>
      smartLists.filter(
        (smartList) => !favoriteKeys.has(shortcutKey({ kind: 'smart', id: smartList.id })),
      ),
    [favoriteKeys, smartLists],
  );
  const favoriteItems = useMemo(
    () =>
      favoriteShortcuts
        .map((shortcut) => {
          if (shortcut.kind === 'project') {
            const project = projects.find((candidate) => candidate.id === shortcut.id);
            return project ? { shortcut, project } : undefined;
          }
          const smartList = smartLists.find((candidate) => candidate.id === shortcut.id);
          return smartList ? { shortcut, smartList } : undefined;
        })
        .filter((item): item is FavoriteItem => item !== undefined),
    [favoriteShortcuts, projects, smartLists],
  );

  const updateFavoriteShortcuts = (shortcuts: FavoriteShortcut[]): void => {
    store.updateUiPreferences({ favoriteShortcuts: shortcuts });
  };

  const addFavoriteShortcut = (shortcut: FavoriteShortcut): void => {
    const key = shortcutKey(shortcut);
    if (favoriteKeys.has(key)) return;
    updateFavoriteShortcuts([...favoriteShortcuts, shortcut]);
  };

  const removeFavoriteShortcut = (shortcut: FavoriteShortcut): void => {
    const key = shortcutKey(shortcut);
    updateFavoriteShortcuts(favoriteShortcuts.filter((item) => shortcutKey(item) !== key));
  };

  const moveFavoriteShortcut = (sourceKey: string, targetKey: string): void => {
    if (sourceKey === targetKey) return;
    const sourceIndex = favoriteShortcuts.findIndex((item) => shortcutKey(item) === sourceKey);
    const targetIndex = favoriteShortcuts.findIndex((item) => shortcutKey(item) === targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const next = [...favoriteShortcuts];
    const [moved] = next.splice(sourceIndex, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);
    updateFavoriteShortcuts(next);
  };

  const handleCreate = (name: string): void => {
    setCreating(false);
    if (!name.trim()) return;
    const project = store.createProject(name);
    if (!project) return;
    onSelectTaskView({ kind: 'project', projectId: project.id });
  };

  return (
    <nav
      aria-label="Primary"
      className={`workspace-sidebar ${mobileHidden ? 'max-md:hidden' : ''}`}
    >
      <div className="mb-5 flex items-center gap-3 px-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-[color:var(--app-border-strong)] bg-[color:var(--app-panel-strong)] text-[color:var(--app-accent)]">
          <CheckSquare2 aria-hidden="true" size={17} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold tracking-tight">Portfolio PM</span>
          <span className="block text-[10px] font-medium uppercase tracking-[0.16em] text-[color:var(--app-text-soft)]">
            Workspace
          </span>
        </span>
      </div>

      <button
        type="button"
        className="ui-button ui-button-primary mb-4 h-10 w-full justify-start gap-2 px-3"
        onClick={() => {
          onNavigate('tasks');
          window.setTimeout(() => document.getElementById('quick-add-input')?.focus(), 0);
        }}
      >
        <Plus aria-hidden="true" size={16} />
        New task
      </button>

      <fieldset className="sidebar-workspace-nav grid gap-1 border-b border-[color:var(--app-border)] pb-4">
        <legend className="sr-only">Choose workspace</legend>
        <RouteButton
          active={route === 'tasks'}
          testId="workspace-tasks"
          onClick={() => onNavigate('tasks')}
        >
          <span className="flex items-center gap-2">
            <CheckSquare2 aria-hidden="true" size={16} />
            Tasks
          </span>
        </RouteButton>
        <RouteButton
          active={route === 'focus'}
          testId="workspace-focus"
          onClick={() => onNavigate('focus')}
        >
          <span className="flex items-center gap-2">
            <Crosshair aria-hidden="true" size={16} />
            Focus
          </span>
        </RouteButton>
        <RouteButton
          active={route === 'okrs'}
          testId="workspace-okrs"
          onClick={() => onNavigate('okrs')}
        >
          <span className="flex items-center gap-2">
            <Target aria-hidden="true" size={16} />
            OKRs
          </span>
        </RouteButton>
        <RouteButton
          active={route === 'agents'}
          testId="workspace-agents"
          onClick={() => onNavigate('agents')}
        >
          <span className="flex items-center gap-2">
            <Bot aria-hidden="true" size={16} />
            Agent Control
          </span>
        </RouteButton>
      </fieldset>

      <ScrollViewport
        className="mt-3 -mr-3 min-h-0 flex-1"
        viewportClassName="h-full"
        contentClassName="sidebar-scroll-content"
      >
        <SidebarSection title="System Views">
          {SYSTEM_VIEWS.map((view) => (
            <SidebarRowButton
              key={view.id}
              active={route === 'tasks' && taskView.kind === 'system' && taskView.id === view.id}
              count={counts.system[view.id]}
              testId={`sidebar-system-${view.id}`}
              onClick={() => {
                onNavigate('tasks');
                onSelectTaskView({ kind: 'system', id: view.id });
              }}
            >
              {view.label}
            </SidebarRowButton>
          ))}
        </SidebarSection>

        <SidebarSection title="Favorites">
          <ul className="space-y-px" data-testid="favorite-list">
            {favoriteItems.length === 0 ? (
              <li className="surface-empty p-3 text-xs">No favorites yet.</li>
            ) : (
              favoriteItems.map((item) => {
                const key = shortcutKey(item.shortcut);
                const isProject = 'project' in item;
                const count = isProject
                  ? (counts.projects.get(item.project.id) ?? 0)
                  : (counts.smartLists.get(item.smartList.id) ?? 0);
                const active =
                  route === 'tasks' &&
                  (isProject
                    ? taskView.kind === 'project' && taskView.projectId === item.project.id
                    : taskView.kind === 'smart' && taskView.smartListId === item.smartList.id);
                return (
                  <FavoriteRow
                    key={key}
                    item={item}
                    count={count}
                    active={active}
                    dragging={draggingFavoriteKey === key}
                    onSelect={() => {
                      onNavigate('tasks');
                      if (isProject) {
                        store.setActiveProject(item.project.id);
                        onSelectTaskView({ kind: 'project', projectId: item.project.id });
                      } else {
                        onSelectTaskView({ kind: 'smart', smartListId: item.smartList.id });
                      }
                    }}
                    onRemove={() => removeFavoriteShortcut(item.shortcut)}
                    onDragStart={() => setDraggingFavoriteKey(key)}
                    onDragEnd={() => setDraggingFavoriteKey(undefined)}
                    onDrop={(sourceKey) => {
                      moveFavoriteShortcut(sourceKey, key);
                      setDraggingFavoriteKey(undefined);
                    }}
                  />
                );
              })
            )}
          </ul>
        </SidebarSection>

        <SidebarSection
          title="Saved Views"
          action={<SavedViewsSectionMenu onCreate={() => setCreatingSmartList(true)} />}
        >
          <ul className="space-y-px" data-testid="smart-list-list">
            {visibleSmartLists.map((smartList) => (
              <SmartListRow
                key={smartList.id}
                smartList={smartList}
                count={counts.smartLists.get(smartList.id) ?? 0}
                active={
                  route === 'tasks' &&
                  taskView.kind === 'smart' &&
                  taskView.smartListId === smartList.id
                }
                editing={smartList.id === editingSmartListId}
                onSelect={() => {
                  onNavigate('tasks');
                  onSelectTaskView({ kind: 'smart', smartListId: smartList.id });
                }}
                onStartEdit={() => setEditingSmartListId(smartList.id)}
                onCommitEdit={(patch) => {
                  store.updateSmartList(smartList.id, patch);
                  setEditingSmartListId(undefined);
                }}
                onCancelEdit={() => setEditingSmartListId(undefined)}
                onDelete={() => store.removeSmartList(smartList.id)}
                isFavorite={favoriteKeys.has(shortcutKey({ kind: 'smart', id: smartList.id }))}
                onAddToFavorites={() => addFavoriteShortcut({ kind: 'smart', id: smartList.id })}
                onRemoveFromFavorites={() =>
                  removeFavoriteShortcut({ kind: 'smart', id: smartList.id })
                }
              />
            ))}
            {creatingSmartList && (
              <li>
                <SmartListForm
                  onCommit={(patch) => {
                    const created = store.createSmartList(patch);
                    setCreatingSmartList(false);
                    if (created) {
                      onNavigate('tasks');
                      onSelectTaskView({ kind: 'smart', smartListId: created.id });
                    }
                  }}
                  onCancel={() => setCreatingSmartList(false)}
                />
              </li>
            )}
          </ul>
        </SidebarSection>

        <SidebarSection
          title="Projects"
          action={
            <ProjectSectionMenu
              sort={uiPreferences.projectSidebarSort}
              countMode={uiPreferences.projectSidebarCount}
              showArchived={uiPreferences.projectSidebarShowArchived}
              onCreate={() => setCreating(true)}
              onChangeSort={(projectSidebarSort) =>
                store.updateUiPreferences({ projectSidebarSort })
              }
              onChangeCountMode={(projectSidebarCount) =>
                store.updateUiPreferences({ projectSidebarCount })
              }
              onChangeShowArchived={(projectSidebarShowArchived) =>
                store.updateUiPreferences({ projectSidebarShowArchived })
              }
            />
          }
        >
          <div className="mb-1 flex items-center gap-2 px-2 text-[11px] text-[color:var(--app-text-soft)]">
            <FolderKanban aria-hidden="true" size={13} />
            <span>Active portfolio</span>
          </div>
          <ul className="space-y-px" data-testid="project-list">
            {visibleProjects.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                count={sidebarProjectCounts.get(project.id) ?? 0}
                active={
                  route === 'tasks'
                    ? taskView.kind === 'project' && taskView.projectId === project.id
                    : project.id === active.id
                }
                editing={project.id === editingId}
                manualSort={uiPreferences.projectSidebarSort === 'manual'}
                dragging={project.id === draggingProjectId}
                onSelect={() => {
                  store.setActiveProject(project.id);
                  onSelectTaskView({ kind: 'project', projectId: project.id });
                }}
                onStartEdit={() => setEditingId(project.id)}
                onCommitEdit={(name) => {
                  store.renameProject(project.id, name);
                  setEditingId(undefined);
                }}
                onCancelEdit={() => setEditingId(undefined)}
                onArchive={() => {
                  store.archiveProject(project.id);
                  if (
                    route === 'tasks' &&
                    taskView.kind === 'project' &&
                    taskView.projectId === project.id
                  ) {
                    onSelectTaskView({ kind: 'project', projectId: INBOX_PROJECT_ID });
                  }
                }}
                onUnarchive={() => {
                  store.unarchiveProject(project.id);
                }}
                isFavorite={favoriteKeys.has(shortcutKey({ kind: 'project', id: project.id }))}
                onAddToFavorites={() => addFavoriteShortcut({ kind: 'project', id: project.id })}
                onRemoveFromFavorites={() =>
                  removeFavoriteShortcut({ kind: 'project', id: project.id })
                }
                onDragStart={() => setDraggingProjectId(project.id)}
                onDragEnd={() => setDraggingProjectId(undefined)}
                onDrop={(sourceProjectId) => {
                  setDraggingProjectId(undefined);
                  if (sourceProjectId === project.id) return;
                  const sourceIndex = visibleProjects.findIndex(
                    (item) => item.id === sourceProjectId,
                  );
                  const targetIndex = visibleProjects.findIndex((item) => item.id === project.id);
                  if (sourceIndex < 0 || targetIndex < 0) return;
                  const next = [...visibleProjects];
                  const [moved] = next.splice(sourceIndex, 1);
                  if (!moved) return;
                  next.splice(targetIndex, 0, moved);
                  store.reorderProjects(next.map((item) => item.id));
                }}
              />
            ))}
            {creating && (
              <li>
                <NameInput
                  placeholder="New project name"
                  onCommit={handleCreate}
                  onCancel={() => setCreating(false)}
                />
              </li>
            )}
          </ul>
        </SidebarSection>

        <SidebarSection title="Tags">
          <ul className="space-y-px" data-testid="tag-list">
            {tags.length === 0 ? (
              <li className="surface-empty p-3 text-xs">No tags yet.</li>
            ) : (
              tags.map(({ tag, count }) =>
                editingTag === tag ? (
                  <li key={tag} className="px-2 py-px">
                    <NameInput
                      initialValue={tag}
                      placeholder="Tag name"
                      onCommit={(nextTag) => {
                        const normalized = nextTag.trim().replace(/^#/, '');
                        if (normalized && normalized !== tag) {
                          for (const task of store.projection.getTasks()) {
                            if (!task.tags.includes(tag)) continue;
                            store.updateTask(task.id, {
                              tags: task.tags.map((item) => (item === tag ? normalized : item)),
                            });
                          }
                          const color = uiPreferences.tagColors[tag];
                          const tagColors = { ...uiPreferences.tagColors };
                          delete tagColors[tag];
                          if (color) tagColors[normalized] = color;
                          store.updateUiPreferences({ tagColors });
                          if (taskView.kind === 'tag' && taskView.tag === tag) {
                            onSelectTaskView({ kind: 'tag', tag: normalized });
                          }
                        }
                        setEditingTag(undefined);
                      }}
                      onCancel={() => setEditingTag(undefined)}
                    />
                  </li>
                ) : (
                  <TagRow
                    key={tag}
                    tag={tag}
                    count={count}
                    color={
                      uiPreferences.tagColors[tag] ?? DEFAULT_TAG_COLORS[hashTag(tag)] ?? '#64748b'
                    }
                    active={route === 'tasks' && taskView.kind === 'tag' && taskView.tag === tag}
                    onSelect={() => {
                      onNavigate('tasks');
                      onSelectTaskView({ kind: 'tag', tag });
                    }}
                    onChangeColor={(color) =>
                      store.updateUiPreferences({
                        tagColors: { ...uiPreferences.tagColors, [tag]: color },
                      })
                    }
                    onRename={() => setEditingTag(tag)}
                  />
                ),
              )
            )}
          </ul>
        </SidebarSection>
      </ScrollViewport>
    </nav>
  );
}

function buildProjectSidebarCounts(
  tasks: Task[],
  projects: Project[],
  countMode: 'active' | 'total',
): Map<string, number> {
  const counts = new Map(projects.map((project) => [project.id, 0]));
  for (const task of tasks) {
    if (task.trashedAt !== undefined) continue;
    if (countMode === 'active' && task.completedAt !== undefined) continue;
    counts.set(task.projectId, (counts.get(task.projectId) ?? 0) + 1);
  }
  return counts;
}

function sortSidebarProjects(projects: Project[], sort: 'alphabetical' | 'manual'): Project[] {
  return [...projects].sort((a, b) => {
    if (sort === 'manual') {
      return a.order - b.order || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    }
    return (
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
      a.order - b.order ||
      a.createdAt - b.createdAt
    );
  });
}
